export interface TrainingSettings {
  /** Copied into each new questionnaire; changing it alters nothing already published. */
  defaultPassMarkPercent: number;
  /** `null` means a certificate never expires. */
  defaultCertificateValidityMonths: number | null;
  retakesAllowedByDefault: boolean;
  reminderDaysBeforeDue: number[];
  overdueReminderEveryDays: number;
  /** What a certificate prints. */
  certificateTitle: string;
  issuerName: string;
  signatoryName: string;
  signatoryTitle: string;
  /** A six-digit hex colour, `#RRGGBB`. */
  accentColour: string;
  /** Whether a logo is set. The storage key is the service's own and never leaves it. */
  hasLogo: boolean;
  updatedById: string | null;
  updatedAt: string;
}

export type UpdateTrainingSettingsInput = Partial<
  Pick<
    TrainingSettings,
    | 'defaultPassMarkPercent'
    | 'defaultCertificateValidityMonths'
    | 'retakesAllowedByDefault'
    | 'reminderDaysBeforeDue'
    | 'overdueReminderEveryDays'
    | 'certificateTitle'
    | 'issuerName'
    | 'signatoryName'
    | 'signatoryTitle'
    | 'accentColour'
  >
>;

export interface UpdateTrainingSettingsPayload {
  input: UpdateTrainingSettingsInput;
  actorId: string;
}

/** `logoKey` null removes the logo. The gateway has already written the object. */
export interface SetTrainingLogoPayload {
  logoKey: string | null;
  actorId: string;
}

export const TRAINING_SETTINGS_LIMITS = {
  maxCertificateValidityMonths: 120,
  maxReminderDays: 10,
  maxReminderDaysBefore: 90,
  maxOverdueEveryDays: 90,
  textMaxLength: 80,
} as const;

/** `#RRGGBB`, either case. */
export const ACCENT_COLOUR_PATTERN = /^#[0-9a-fA-F]{6}$/;

/** Folder inside the shared bucket (the deployment's key prefix is added by the storage adapter). */
export const TRAINING_LOGO_KEY_FOLDER = 'training/branding';

export const TRAINING_LOGO_EXTENSIONS = ['png', 'jpg'] as const;
export type TrainingLogoExtension = (typeof TRAINING_LOGO_EXTENSIONS)[number];

export const TRAINING_LOGO_MAX_BYTES = 1024 * 1024;

export const trainingLogoKey = (id: string, extension: TrainingLogoExtension): string =>
  `${TRAINING_LOGO_KEY_FOLDER}/logo-${id}.${extension}`;

const LOGO_KEY_PATTERN = new RegExp(
  `^${TRAINING_LOGO_KEY_FOLDER}/logo-[0-9a-f-]{36}\\.(${TRAINING_LOGO_EXTENSIONS.join('|')})$`,
);

/** The file name of the sample certificate the settings page downloads. */
export const TRAINING_CERTIFICATE_PREVIEW_FILE_NAME = 'certificate-preview.pdf';

/** The service refuses any other key, so a settings write can never point a read at another object. */
export const isTrainingLogoKey = (key: string): boolean => LOGO_KEY_PATTERN.test(key);
